import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';

const LIST_REGEX = /\s*,\s*/;

export interface ScrolltoObject {
    productIds?: string;
    _pageYOffset?: number;
}
export class Ui {
    constructor(
        public webapp: boolean,
        public isRetina: boolean,
        public navigator: any,
        public device: {
            mobile: boolean;
            tablet: boolean;
            desktop: boolean;
        },
        public res: {
            mobile: boolean;
            tablet: boolean;
            desktop: boolean;
        }
    ) {}
}

export const initialUi = {
    device: {
        mobile: false,
        tablet: false,
        desktop: false,
    },
    res: {
        mobile: false,
        tablet: false,
        desktop: false,
    },
    webapp: false,
    isRetina: false,
    navigator: {},
};

@Injectable()

/**
 * Servicio para la interacción con el UI de la aplicación
 */
export class UiService {
    public uiState$: Observable<Ui>;

    public ui: Ui = initialUi;

    // private breakpoints: BreakPoint[] = [];
    public rules: Record<string, string>;

    public readonly defaultRules = {
        xs: '(max-width: 767px)',
        sm: '(min-width: 768px) and (max-width: 991px)',
        md: '(min-width: 992px) and (max-width: 1279px)',
        lg: '(min-width: 1280px)',
    };

    private readonly pageYOffsetSubject = new Subject<{ productIds?: string; _pageYOffset?: number }>();

    public pageYOffsetSubject$ = this.pageYOffsetSubject.asObservable();

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly platformService: CorePlatformService
    ) {
        // if (this.cmsConfigService.config) {
        //   this.breakpoints = this.cmsConfigService.config.breakpoints;
        // }
        this.setInfoDevice();
        this.updateOnResizeUI();
        this.uiState$ = fromEvent(this.window, 'resize').pipe(
            debounceTime(200),
            map(() => this.updateOnResizeUI())
        );
    }

    public get() {
        return this.ui;
    }

    public setRules(rules: Record<string, string>) {
        this.rules = rules;
    }

    public scrollToElement(el: ElementRef) {
        if (this.platformService.isBrowser()) {
            this.window.scrollTo({
                top: this.offsetElement(el).top,
                left: this.offsetElement(el).left,
                behavior: 'smooth',
            });
        }
    }

    public getAfklClasses(desktopRatio: string | null, tabletRatio: string | null, mobileRatio: string | null): string {
        let desktop: string = desktopRatio;
        let tablet: string = tabletRatio;
        let mobile: string = mobileRatio;

        if (!desktopRatio) {
            desktop = '16-9';
        }
        if (!tabletRatio) {
            tablet = '16-9';
        }
        if (!mobileRatio) {
            mobile = '16-9';
        }

        let outputClasses = 'afkl-lazy-wrapper';

        outputClasses += ` lg-afkl-img-ratio-${desktop}`;
        outputClasses += ` md-afkl-img-ratio-${tablet}`;
        outputClasses += ` afkl-img-ratio-${mobile}`;

        return outputClasses;
    }

    public is(list: string[] | string) {
        const workList: string[] = [...this.assureList(list)];
        return workList.includes(this.getCurrentMatch());
    }

    private setInfoDevice() {
        if (this.platformService.isBrowser()) {
            this.ui.navigator = {
                // eslint-disable-next-line deprecation/deprecation
                appName: this.window.navigator.appName,
                cookieEnabled: this.window.navigator.cookieEnabled,
                // eslint-disable-next-line deprecation/deprecation
                platform: this.window.navigator.platform,
            };
            // TODO ECOMDUTI-21235 [TechDebt] - CorePlatformService.isNativeApp() should be used instead of this
            this.ui.webapp = String(this.window.navigator.userAgent).toLowerCase().includes('massimodutti_ecom');
            this.ui.isRetina = this.window.devicePixelRatio > 1
                || this.window.matchMedia?.(
                    '(-webkit-min-device-pixel-ratio: 1.5),(-moz-min-device-pixel-ratio: 1.5),(min-device-pixel-ratio: 1.5),(min-resolution: 192dpi),(min-resolution: 2dppx)'
                ).matches;
        }
    }

    private updateOnResizeUI() {
        if (this.platformService.isBrowser()) {
            if (this.window.outerWidth < 768) {
                this.ui.device.mobile = true;
                this.ui.device.tablet = false;
                this.ui.device.desktop = false;
            } else if (this.window.outerWidth < 992 || this.window.outerWidth > 768 && this.ui.webapp) {
                this.ui.device.mobile = false;
                this.ui.device.tablet = true;
                this.ui.device.desktop = false;
            } else {
                this.ui.device.mobile = false;
                this.ui.device.tablet = false;
                this.ui.device.desktop = true;
            }

            const currentWidth: number = this.window.innerWidth;
            this.ui.res.mobile = currentWidth < 768;
            this.ui.res.tablet = currentWidth >= 768 && currentWidth < 992;
            this.ui.res.desktop = currentWidth >= 992;

            return this.ui;
        }
    }

    private offsetElement(el) {
        if (this.platformService.isBrowser()) {
            const rect = el.nativeElement.getBoundingClientRect();
            // eslint-disable-next-line deprecation/deprecation
            const scrollLeft = this.window.pageXOffset || this.document.documentElement.scrollLeft;
            // eslint-disable-next-line deprecation/deprecation
            const scrollTop = this.window.pageYOffset || this.document.documentElement.scrollTop;

            return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
        }
    }

    private assureList(list: string[] | string) {
        return typeof list === 'string' ? list.split(LIST_REGEX) : list;
    }

    private getCurrentMatch() {
        if (this.platformService.isBrowser()) {
            const rules = this.rules || this.defaultRules;

            // eslint-disable-next-line no-restricted-syntax
            for (const property in rules) {
                // biome-ignore lint/suspicious/noPrototypeBuiltins: <explanation>
                if (rules.hasOwnProperty(property)) {
                    if (this.window.matchMedia(rules[property]).matches) {
                        return property;
                    }
                }
            }
        }
    }

    // almacena altura del scroll en el category-view
    // eslint-disable-next-line deprecation/deprecation
    public setPageYOffsetSubject(scrolltoObject: ScrolltoObject = { _pageYOffset: this.window.pageYOffset }) {
        this.pageYOffsetSubject.next(scrolltoObject);
    }

    /**
     * Función para obtener la posición Y del scroll de ventana
     */
    public getYScrollPosition(): number {
        if (this.platformService.isBrowser()) {
            const window = this.document.defaultView;

            return window.scrollY;
        }
    }

    /**
     * Función para aplicar clases al body
     */
    public addClassToBody(className: string): void {
        if (this.platformService.isBrowser()) {
            this.document.body.classList.add(className);
        }
    }

    /**
     * Función para quitar clases al body
     */
    public removeClassFromBody(className: string): void {
        if (this.platformService.isBrowser()) {
            this.document.body.classList.remove(className);
        }
    }

    /**
     * Función para aplicar atributos al body
     */
    public addAttributeToBody(attribute: string, value: string): void {
        if (this.platformService.isBrowser()) {
            this.document.body.setAttribute(attribute, value);
        }
    }
}
